import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Link } from 'react-router-dom';
import { Check, Circle, ChevronRight, ArrowDownLeft } from 'lucide-react';

import { cn } from '@client';
import { selectSessionAdvisor, selectAdvisorOnboardingCompletedStepsData } from '@client/model';

import { type OnboardingStepId } from '.';

const onboardingSteps: { id: OnboardingStepId; label: string }[] = [
  { id: 'welcome', label: 'Welcome to Waterlily' },
  { id: 'invite-client', label: 'Inviting Clients' },
  { id: 'client-intake-form', label: 'Client Intake Form' },
  { id: 'complete-profile', label: 'Complete Profile' },
];

export function OnboardingProgressWidget() {
  const [isExpanded, setIsExpanded] = useState(true);

  const advisor = useSelector(selectSessionAdvisor);
  const onboardingData = useSelector(selectAdvisorOnboardingCompletedStepsData);

  const completedSteps = onboardingData?.completedSteps ?? [];
  const completedCount = onboardingSteps.filter(step => completedSteps.includes(step.id)).length;

  const totalSteps = onboardingSteps.length;
  const percentage = Math.round((completedCount / totalSteps) * 100);
  const isAllStepsCompleted = completedCount === totalSteps;

  const firstIncompleteStep = onboardingSteps.find(step => !completedSteps.includes(step.id));
  const continueStepPath = firstIncompleteStep?.id ?? 'completion';

  if (!advisor || isAllStepsCompleted) {
    return null;
  }

  return (
    <div
      className={cn(
        'cursor-pointer rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm transition-all duration-200',
        {
          'fixed bottom-4 left-4 w-[256px]': isExpanded,
        },
      )}
      onClick={() => setIsExpanded(true)}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-xs font-semibold text-gray-950">Onboarding Progress</h3>
          <span className="text-xs font-medium text-gray-700">
            {percentage}%&nbsp;&nbsp;•&nbsp;&nbsp;Completed
          </span>
        </div>
        <button
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();

            setIsExpanded(current => !current);
          }}
        >
          <ArrowDownLeft
            className={cn(
              'size-4 cursor-pointer text-gray-700 transition-all hover:text-gray-950',
              {
                'hover:-translate-x-0.5 hover:translate-y-0.5': isExpanded,
                'rotate-180 hover:-translate-y-0.5 hover:translate-x-0.5': !isExpanded,
              },
            )}
          />
        </button>
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="bg-primary h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <ul className="mt-3 space-y-1.5">
            {onboardingSteps.map(step => {
              const isCompleted = completedSteps.includes(step.id);
              return (
                <li key={step.id}>
                  <Link
                    to={`/advisors/${advisor.advisorId}/onboarding/${step.id}`}
                    className={cn(
                      'flex items-center gap-2 text-xs transition-colors',
                      isCompleted
                        ? 'text-gray-500 hover:text-gray-700'
                        : 'hover:text-primary text-gray-700',
                    )}
                  >
                    {isCompleted ? (
                      <Check className="size-3.5 text-green-500" />
                    ) : (
                      <Circle className="size-3.5 text-gray-300" />
                    )}
                    <span className={cn(isCompleted && 'line-through')}>{step.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>

          <Link
            to={`/advisors/${advisor.advisorId}/onboarding/${continueStepPath}`}
            className="text-primary hover:text-primary-dark mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium transition-colors hover:bg-gray-50"
          >
            Continue Onboarding
            <ChevronRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
